import React, { useState, useMemo, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import { TotalSalesForm } from "./TotalSalesForm";
import { DetailsModal } from "../../grid/DetailsModal";
import ErrorHandler from "./../../core/ErrorHandler";
import Accordion from "../../Accordion";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { ConstructAggregateSetting } from "./helper/ConstructAggregateSetting";
import { GridColumn } from "@progress/kendo-react-grid";
import { ZIndexContext } from "@progress/kendo-react-common";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";
import { GetISODate, DateWithoutTimeAsFormattedString } from "shared/helpers/DateHelpers";

import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../grid/Kendo/KendoGridAndColumnConstants";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
  AggregateNumberFooterCell,
  TooltipCell,
} from "../../grid/Kendo/CustomKendoGridCells";

export function TotalSalesByPayer(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const [submittedFormData, setSubmittedFormData] = useState({});
  const [mainGridColumns, setMainGridColumns] = useState([]);
  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  const [mainGridAggregates, setMainGridAggregates] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [detailError, setDetailError] = useState({});
  const [error, setError] = useState({});
  let detailGridAggregateColumnSettings = [
    { aggregate: "sum", field: "AdjudicatedAmount" },
    { aggregate: "sum", field: "PaidAmount" },
    { aggregate: "sum", field: "CoPay" },
  ];
  const [detailGridAggregates, setDetailGridAggregates] = useState([]);
  const [detailsGridApiEndpoint, setDetailsGridApiEndpoint] = useState("");
  const [detailsGridParameters, setDetailsGridParameters] = useState({});

  let { mutate: mutateForServerSideExport } = useInsertServerSideExport();

  const _container = React.useRef();
  const clientGridRef = useRef();
  const clientDetailGridRef = useRef();
  const sendMainAggregatesToParent = (aggregatesFromSharedTotalSalesGrid) => {
    setMainGridAggregates(aggregatesFromSharedTotalSalesGrid.data);
  };

  const sendDetailAggregatesToParent = (aggregatesFromSharedTotalSalesGrid) => {
    setDetailGridAggregates(aggregatesFromSharedTotalSalesGrid.data);
  };

  const payerApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (IsNullOrEmpty(submittedFormData.selectedLocation)) {
      return {};
    }

    let params = {
      user: user,
      toDate: DateWithoutTimeAsFormattedString(submittedFormData.toDate),
      fromDate: DateWithoutTimeAsFormattedString(submittedFormData.fromDate),
      dateType: submittedFormData.dateType,
      paidAmountType: submittedFormData.paidAmountType,
      includeCopay: submittedFormData.includeCopay,
      hideIgnored: submittedFormData.omitIgnored,
      mmids: submittedFormData.selectedLocation,
      includeClid2: submittedFormData.includeClid2,
    };

    return params;
  }, [submittedFormData]);


  function handleFormSubmit(e) {
    setSubmittedFormData(e);
    InitiateGridDataStateReset();
  }

  function CreateGridColumn(column) {
    let columnWidth = DefaultColumnWidth();

    let filterType = "text";
    if (column.type === "money" || column.type === "number") {
      filterType = "numeric";
    }

    if (column.type === "money") {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          locked={column.locked}
          cells={{
            data: MainGridMoneyCell,
            footerCell: MainGridAggregateMoneyFooterCell,
          }}
          footerCell={MainGridAggregateMoneyFooterCell}
          headerCell={TooltipCell}
        />
      );
    } else if (column.type === "number") {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          cells={{
            footerCell: AggregateFooterCell,
          }}
          footerCell={AggregateFooterCell}
          headerCell={TooltipCell}
        />
      );
    } else if (column.locked) {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          locked={true}
          headerCell={TooltipCell}
        />
      );
    } else {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          headerCell={TooltipCell}
        />
      );
    }
  }

  function getPayerDetails(props) {
    setShowModal(true);
    var parameters1 = {};
    if (props.isAggregate) {
      parameters1 = {
        user: user,
        payer: props.pushPayerName.toString(),
        searchDate: props.field,
        searchStartDate: DateWithoutTimeAsFormattedString(props.startDate),
        searchEndDate: DateWithoutTimeAsFormattedString(props.endDate),
        hideIgnored: submittedFormData.omitIgnored,
        mmids: submittedFormData.selectedLocation,
        DateType: submittedFormData.dateType,
        paidAmountType: submittedFormData.paidAmountType,
        includeClid2: submittedFormData.includeClid2,
      }

      setDetailsGridApiEndpoint("api/TotalSales/GetByPayerAggregateDetails");
      setDetailsGridParameters(parameters1);
    }
    else {
      if (props.field == "TotalSales") {
        parameters1 = {
          user: user,
          payer: props.dataItem["PayerName"],
          searchStartDate: DateWithoutTimeAsFormattedString(submittedFormData.fromDate),
          searchEndDate: DateWithoutTimeAsFormattedString(submittedFormData.toDate),
          hideIgnored: submittedFormData.omitIgnored,
          mmids: submittedFormData.selectedLocation,
          DateType: submittedFormData.dateType,
          paidAmountType: submittedFormData.paidAmountType,
          includeClid2: submittedFormData.includeClid2,
        }
        setDetailsGridApiEndpoint("api/TotalSales/GetByPayerAggregateDetails");
        setDetailsGridParameters(parameters1);
      }
      else {
        parameters1 = {
          user: user,
          payer: props.dataItem["PayerName"],
          searchDate: props.field,
          dateType: submittedFormData.dateType,
          hideIgnored: submittedFormData.omitIgnored,
          mmids: submittedFormData.selectedLocation,
          paidAmountType: submittedFormData.paidAmountType,
          includeClid2: submittedFormData.includeClid2,
        }
        setDetailsGridApiEndpoint("api/TotalSales/GetByPayerDetails");
        setDetailsGridParameters(parameters1);
      }
    }
  }
  let MainGridMoneyCell = (props) => {
    let onCellClick = () => {
      let inputProps = {
        ...props,
        isAiggregate: false
      };
      getPayerDetails(inputProps);
    };

    let options = {
      onClick: onCellClick,
    };

    return MoneyCell(props, options);
  };

  let AggregateFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };
    return AggregateNumberFooterCell(props, options);
  };

  let DetailsGridAggregateMoneyCell = (props) => {
    var options = {
      aggregateData: detailGridAggregates,
    };
    return AggregateMoneyFooterCell(props, options);
  };

  const MainGridAggregateMoneyFooterCell = (props) => {
    let onCellClick = () => {
      var startDate;
      var endDate;
      if (props.field == "TotalSales") {
        startDate = DateWithoutTimeAsFormattedString(submittedFormData.fromDate);
        endDate = DateWithoutTimeAsFormattedString(submittedFormData.toDate);
      } else {
        startDate = props.field;
        endDate = props.field;
      }

      const filteredDataFromComponent = clientGridRef.current
        ? clientGridRef.current.GetGridFilteredData()
        : [];

      let allFilteredPayers = "";
      if (filteredDataFromComponent) {
        filteredDataFromComponent.forEach((element) => {
          allFilteredPayers = allFilteredPayers + element["PayerName"] + "|";
        });
      } else {
        alert("No data is displayed on grid");
        return null;
      }

      getPayerDetails({
        isAggregate: true,
        pushPayerName: allFilteredPayers,
        startDate: DateWithoutTimeAsFormattedString(startDate),
        endDate: DateWithoutTimeAsFormattedString(endDate)
      })
    };

    var options = {
      aggregateData: mainGridAggregates,
      onClick: onCellClick,
    };
    return AggregateMoneyFooterCell(props, options);
  };


  function detailsGridErrorHandlerCallback(error) {
    let exportRequestParameters = { ...detailsGridParameters };

    if (exportRequestParameters.searchDate) {
      exportRequestParameters.searchDate = GetISODate(exportRequestParameters.searchDate);
    }
    if (exportRequestParameters.searchStartDate) {
      exportRequestParameters.searchStartDate = GetISODate(exportRequestParameters.searchStartDate);
    }
    if (exportRequestParameters.searchEndDate) {
      exportRequestParameters.searchEndDate = GetISODate(exportRequestParameters.searchEndDate);
    }

    let apiEndpoint = "";

    if (detailsGridApiEndpoint === "api/TotalSales/GetByPayerDetails") {
      apiEndpoint = "api/TotalSales/PostTotalSalesByPayerDetailsExcelExportRequestToAzureStorage";
    }
    else if (detailsGridApiEndpoint === "api/TotalSales/GetByPayerAggregateDetails") {
      apiEndpoint = "api/TotalSales/PostTotalSalesByPayerAggregateDetailsExcelExportRequestToAzureStorage";
    }

    let mutateParameter = {
      error: error,
      endpoint: apiEndpoint,
      directParameters: exportRequestParameters
    };

    mutateForServerSideExport(mutateParameter);
  }

  function setGridData(data) {
    let columns = [];

    if (data.length > 0) {

      let countColumnTitle = "Claim Count";

      if (payerApiParams && payerApiParams.dateType === "checkDate") {
        countColumnTitle = "Payment Count";
      }

      let headerProps = {
        PayerName: { title: "Payer Name", locked: true },
        TotalSales: {
          title: "Total Sales",
          type: "money",
          showTotal: true,
          locked: true,
        },
      };
      Object.keys(data[0]).map((key) => {
        return columns.push({
          field: key,
          title: headerProps[key]
            ? headerProps[key].title
            : key.includes("Count")
              ? countColumnTitle
              : key,
          type: headerProps[key]
            ? headerProps[key].type
            : key.includes("Count")
              ? "number"
              : "money",
          showTotal: headerProps[key] ? headerProps[key].showTotal : true,
          fixedLeft: headerProps[key] ? true : false,
          selectable: headerProps[key] || key.includes("Count") ? false : true,
          locked: headerProps[key] ? headerProps[key].locked : false,
        });
      });

      setError({});
    } else {
      setError({ status: 201, Message: "No data found for given parameters" });
    }
    setMainGridColumns(columns);

  }

  return (
    <div className="feature-tab" id="total-sales-by-payer">
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />
      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <TotalSalesForm handleFormSubmit={handleFormSubmit} _uid="TSP" />
          </Col>
        </Row>
      </Accordion>
      <div ref={_container}>
        <Row>
          <Col className="tight-grid multi-line-filter">
            <SharedMainSelectableClientGrid
              ref={clientGridRef}
              apiGetEndpoint={"api/TotalSales/GetByPayer"}
              apiParameters={payerApiParams}
              aggregateColumnSettings={ConstructAggregateSetting(new Date(submittedFormData.fromDate), new Date(submittedFormData.toDate))}
              sendAggregatesParentCallback={sendMainAggregatesToParent}
              styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
              onDataStateChangedCallback={() => {
                const allData = clientGridRef.current
                  ? clientGridRef.current.GetAllGridData()
                  : [];
                if (allData) {
                  setGridData(allData);
                }
              }}
              gridDataStateResetProcessingInitiated={
                gridDataStateResetProcessingInitiated
              }
              onGridDataStateResetCompleted={onGridDataStateResetCompleted}
              exportFileNamePrefix={"TotalSalesByPayer"}>

              {mainGridColumns.length > 0
                ? mainGridColumns.map((singleColumn) =>
                  CreateGridColumn(singleColumn)
                )
                : null}
            </SharedMainSelectableClientGrid>
          </Col>
        </Row>
      </div>
      <DetailsModal
        title="Total Sales Detail"
        show={showModal}
        handleClose={() => setShowModal(false)}
      >
        <ErrorHandler
          error={detailError}
          onClose={() => {
            setDetailError({});
          }}
        />
        <ZIndexContext.Provider value={10003}>
          <Row>
            <Col className="tight-grid multi-line-filter">
              <SharedMainSelectableClientGrid
                ref={clientDetailGridRef}
                apiGetEndpoint={detailsGridApiEndpoint}
                apiParameters={detailsGridParameters}
                aggregateColumnSettings={detailGridAggregateColumnSettings}
                sendAggregatesParentCallback={sendDetailAggregatesToParent}
                styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
                gridErrorHandlerCallback={detailsGridErrorHandlerCallback}
                useQueryOptions={{ refetchOnWindowFocus: false }}
                exportFileNamePrefix={"TotalSalesByPayerDetails"}>
                <GridColumn
                  key={"NCPDP"}
                  field={"NCPDP"}
                  title={"NCPDP"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Script"}
                  field={"Script"}
                  title={"Prescription"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"DispensedDate"}
                  field={"DispensedDate"}
                  title={"Dispensed Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"TransactionDate"}
                  field={"TransactionDate"}
                  title={"Transaction Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Payer"}
                  field={"Payer"}
                  title={"Payer Name"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"BIN"}
                  field={"BIN"}
                  title={"BIN"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"PCN"}
                  field={"PCN"}
                  title={"PCN"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"AdjudicatedAmount"}
                  field={"AdjudicatedAmount"}
                  title={"Promise To Pay"}
                  width={DefaultColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: payerApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null,
                  }}
                  footerCell={payerApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"PaidAmount"}
                  field={"PaidAmount"}
                  title={"Remittance Paid"}
                  width={DefaultColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: DetailsGridAggregateMoneyCell,
                  }}
                  footerCell={DetailsGridAggregateMoneyCell}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"CoPay"}
                  field={"CoPay"}
                  title={"Co-Pay"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: payerApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null,
                  }}
                  footerCell={payerApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"NDC"}
                  field={"NDC"}
                  title={"NDC"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Quantity"}
                  field={"Quantity"}
                  title={"Qty"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"DaysSupply"}
                  field={"DaysSupply"}
                  title={"Days Supply"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"AuthorizationID"}
                  field={"AuthorizationID"}
                  title={"Authorization ID"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"CardholderID"}
                  field={"CardholderID"}
                  title={"Cardholder ID"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckNo"}
                  field={"LastCheckNo"}
                  title={"Last Received Check #"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckDate"}
                  field={"LastCheckDate"}
                  title={"Last Received Check Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckPaymentDate"}
                  field={"LastCheckPaymentDate"}
                  title={"Last Received Check Payment Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"ArchiveDate"}
                  field={"ArchiveDate"}
                  title={"Payment/Check Archive Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
              </SharedMainSelectableClientGrid>
            </Col>
          </Row>
        </ZIndexContext.Provider>
      </DetailsModal>
    </div>
  );
}
